import Immutable from 'immutable';

import AuthenticationMethod from 'sharedreact/data/AuthenticationMethod';
import ErrorData from 'sharedreact/data/ErrorData';
import User from 'sharedreact/data/User';
import UserWithPassword from 'sharedreact/data/UserWithPassword';
import ConnectionSupport from 'sharedreact/utils/ConnectionSupport';

type OnLoginSuccess = (user: User, sessionTimeout?: number, maxExtendTimes?: number, locations?: Immutable.List<string>) => void;
type OnError = (error: ErrorData) => void;
type OnLogoutSuccess = () => void;
type OnGetLogonMethodsSuccess = (logonMethods: Immutable.List<AuthenticationMethod>) => void;
type OnForgotPasswordSuccess = (nrOfMailsSent: number) => void;
type OnResetPasswordSuccess = (user: User) => void;
type OnChangePasswordSuccess = () => void;
type OnGetSessionIdSuccess = (sessionId: string) => void;
type OnGetServerVersion = (version: string) => void;

function b64EncodeUnicode (str: string) {
  // first we use encodeURIComponent to get percent-encoded UTF-8,
  // then we convert the percent encodings into raw bytes which
  // can be fed into btoa.
  const toSolidBytes = (match: string, p1: string) => {
    return String.fromCharCode(parseInt('0x' + p1));
  };

  return btoa(encodeURIComponent(str).replace(/%([0-9A-F]{2})/g, toSolidBytes));
}

function getLogonMethods (
  onSuccess: OnGetLogonMethodsSuccess,
  onError: OnError
) {
  ConnectionSupport.post(
    'services/getLogonMethods',
    {},
    (data) => onSuccess(Immutable.List(data.map((item: object) => new AuthenticationMethod(item)))),
    onError,
    false
  );
}

function doLogin (
  userName: string,
  password: string,
  version: number,
  onSuccess: OnLoginSuccess,
  onError: OnError
) {
  ConnectionSupport.post(
    'services/login',
    {authType: 'Basic', cred: b64EncodeUnicode(userName + ':' + password), version: version},
    (data) => {
      onSuccess(
        new User(data.user),
        (data.sessionTimeout === -1 ? undefined : data.sessionTimeout),
        (data.sessionMaxExtendTimes === -1 ? undefined: data.sessionMaxExtendTimes),
        Immutable.List(data.locations)
      );
    },
    onError,
    false
  );
}

function doRelogin (onSuccess: OnLoginSuccess, onError: OnError) {
  ConnectionSupport.post(
    'services/relogin',
    {},
    (data) => {
      onSuccess(
        new User(data.user),
        (data.sessionTimeout === -1 ? undefined : data.sessionTimeout),
        (data.sessionMaxExtendTimes === -1 ? undefined: data.sessionMaxExtendTimes),
        Immutable.List(data.locations)
      );
    },
    onError,
    false
  );
}

function doLogout (onSuccess: OnLogoutSuccess, onError: OnError) {
  ConnectionSupport.post(
    'services/logout',
    {},
    onSuccess,
    onError,
    false
  );
}

function doLoginWithOkta (
  oktaLoginCode: string,
  idpName: string,
  onSuccess: OnLoginSuccess,
  onError: OnError
) {
  ConnectionSupport.post(
    'services/loginWithOkta',
    {
      code: oktaLoginCode,
      idpName: idpName
    },
    (data) => {
      onSuccess(
        new User(data.user),
        (data.sessionTimeout === -1 ? undefined : data.sessionTimeout),
        (data.sessionMaxExtendTimes === -1 ? undefined: data.sessionMaxExtendTimes),
        Immutable.List(data.locations)
      );
    },
    onError,
    false
  );
}

function doReloginWithId(
  sessionId: string,
  onSuccess: OnLoginSuccess,
  onError: OnError
) {
  ConnectionSupport.post(
    'services/reloginWithId',
    {
      'sessionId': sessionId
    },
    (data) => {
      onSuccess(
        new User(data.user),
        (data.sessionTimeout === -1 ? undefined : data.sessionTimeout),
        (data.sessionMaxExtendTimes === -1 ? undefined: data.sessionMaxExtendTimes),
        Immutable.List(data.locations)
      );
    },
    onError,
    false
  );
}

function doForgotPassword (
  userNameOrEmail: string,
  language: string,
  onSuccess: OnForgotPasswordSuccess,
  onError: OnError
) {
  ConnectionSupport.post(
    'services/forgotPassword',
    {
      'userNameOrEmail': userNameOrEmail,
      'language': language
    },
    onSuccess,
    onError,
    false
  );
}

function doResetPassword (
  password: string,
  identifier: string,
  onSuccess: OnResetPasswordSuccess,
  onError: OnError
) {
  ConnectionSupport.post(
    'services/resetPassword',
    {
      'password': password,
      'identifier': identifier
    },
    (data) => { onSuccess(new User(data.user)); },
    onError,
    false
  );
}

function doChangePassword (
  currentUserWithPassword: UserWithPassword,
  newPassword: string,
  onSuccess: OnChangePasswordSuccess,
  onError: OnError
) {
  ConnectionSupport.post(
    'services/changePassword',
    {
      'currentUserWithPassword': currentUserWithPassword,
      'newPassword': newPassword
    },
    onSuccess,
    onError,
    false
  );
}

function getSessionId (
  onSuccess: OnGetSessionIdSuccess,
  onError: OnError
) {
  ConnectionSupport.post(
    'services/getSessionId',
    {},
    onSuccess,
    onError,
    false
  );
}

function getServerVersion (
  onSuccess: OnGetServerVersion,
  onError: OnError
) {
  ConnectionSupport.post(
    'services/getServerVersion',
    {}, 
    (data) => onSuccess(data.version),
    onError,
    false
  );
}

export default {
  getLogonMethods,
  doLogin,
  doRelogin,
  doLogout,
  doLoginWithOkta,
  doReloginWithId,
  doForgotPassword,
  doResetPassword,
  doChangePassword,
  getSessionId,
  getServerVersion
};
